var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Authentication Context
 * Provides authentication state and methods throughout the app
 *
 * Combines Azure AD SSO (MSAL) with JWT-based API authentication:
 * 1. User logs in via Azure AD
 * 2. MSAL token is exchanged for JWT access + refresh tokens
 * 3. JWT is used for all API calls
 * 4. Refresh token (httpOnly cookie) enables silent re-authentication
 */
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus, InteractionRequiredAuthError } from '@azure/msal-browser';
import { loginRequest } from '../auth-config';
import { tokenService } from './tokenService';
const AuthContext = createContext(undefined);
export const AuthProvider = ({ children }) => {
    const { instance, accounts, inProgress } = useMsal();
    const isMsalAuthenticated = useIsAuthenticated();
    const [state, setState] = useState({
        isAuthenticated: false,
        isLoading: true,
        user: null,
        error: null,
        loadingPhase: 'awaiting-msal',
    });
    // Track if we've already initialized to prevent double-init
    const initialized = useRef(false);
    /**
     * Exchange MSAL token for JWT tokens
     */
    const exchangeMsalToken = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const account = accounts[0] || instance.getActiveAccount();
            if (!account) {
                console.error('No MSAL account found');
                return false;
            }
            console.log('Exchanging MSAL token for user:', account.username);
            // Get MSAL access token silently with a 10 s timeout.
            // acquireTokenSilent can hang indefinitely if Azure AD is slow or
            // the hidden iframe is blocked (e.g. self-signed cert). Racing
            // against a timeout ensures we always fall through to a redirect
            // login instead of leaving the user on a permanent loading screen.
            const SILENT_TIMEOUT_MS = 10000;
            const silentTokenPromise = instance.acquireTokenSilent(Object.assign(Object.assign({}, loginRequest), { account }));
            const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error('acquireTokenSilent timed out')), SILENT_TIMEOUT_MS));
            const response = yield Promise.race([silentTokenPromise, timeoutPromise]);
            console.log('Got MSAL token, exchanging for JWT...');
            // Use ID token for authentication (access token is for Graph API)
            // ID token identifies the user to our application
            const tokenToExchange = response.idToken || response.accessToken;
            yield tokenService.exchangeToken(tokenToExchange);
            // Get user info
            const user = yield tokenService.getCurrentUser();
            console.log('JWT auth successful for:', user === null || user === void 0 ? void 0 : user.email);
            setState({
                isAuthenticated: true,
                isLoading: false,
                user,
                error: null,
                loadingPhase: 'ready',
            });
            return true;
        }
        catch (error) {
            console.error('Failed to exchange MSAL token:', error);
            // If interaction is required, the user needs to re-login
            if (error instanceof InteractionRequiredAuthError) {
                setState(prev => (Object.assign(Object.assign({}, prev), { isLoading: false, loadingPhase: 'ready', error: 'Session expired. Please login again.' })));
            }
            else {
                setState(prev => (Object.assign(Object.assign({}, prev), { isLoading: false, loadingPhase: 'ready', error: 'Authentication failed' })));
            }
            return false;
        }
    }), [accounts, instance]);
    /**
     * Initialize auth on mount
     * Wait for MSAL to complete any redirects, then try to authenticate
     */
    useEffect(() => {
        const initAuth = () => __awaiter(void 0, void 0, void 0, function* () {
            // Wait for MSAL to finish processing (especially redirect handling)
            if (inProgress !== InteractionStatus.None) {
                console.log('MSAL interaction in progress:', inProgress);
                return;
            }
            // Prevent double initialization
            if (initialized.current)
                return;
            initialized.current = true;
            console.log('Auth init - MSAL authenticated:', isMsalAuthenticated, 'accounts:', accounts.length);
            // No MSAL accounts in this tab — but httpOnly cookies are domain-
            // scoped, not tab-scoped. A valid refresh-token cookie IS present
            // when the user already authenticated in another tab (e.g. they
            // clicked a deep link that opened a new tab). Try /auth/refresh
            // before giving up and forcing a full login.
            if (!isMsalAuthenticated && accounts.length === 0) {
                console.log('No MSAL session in this tab — attempting cookie-based silent refresh…');
                setState(prev => (Object.assign(Object.assign({}, prev), { loadingPhase: 'restoring' })));
                const hasSession = yield tokenService.checkSession();
                if (hasSession) {
                    console.log('Cookie-based silent refresh succeeded — tab authenticated without MSAL round-trip');
                    const user = yield tokenService.getCurrentUser();
                    setState({
                        isAuthenticated: true,
                        isLoading: false,
                        user,
                        error: null,
                        loadingPhase: 'ready',
                    });
                    return;
                }
                // Cookie absent or expired — user genuinely needs to log in
                console.log('Cookie refresh failed — user needs to login');
                setState(prev => (Object.assign(Object.assign({}, prev), { isLoading: false, loadingPhase: 'ready' })));
                return;
            }
            try {
                // Two paths on app load:
                //   (a) We have a session-hint marker → a previous /auth/token or
                //       /auth/refresh succeeded in this browser session, so a
                //       refresh-token cookie *probably* still exists. Try a
                //       silent /auth/refresh to restore the JWT session without
                //       a fresh MSAL round-trip.
                //   (b) No marker → first login (or the user logged out). Skip
                //       the guaranteed-401 /auth/refresh call and go straight
                //       to the MSAL token exchange.
                if (tokenService.hasSessionHint()) {
                    console.log('Session hint present — attempting silent JWT refresh…');
                    setState(prev => (Object.assign(Object.assign({}, prev), { loadingPhase: 'restoring' })));
                    const hasSession = yield tokenService.checkSession();
                    if (hasSession) {
                        console.log('Existing JWT session restored');
                        const user = yield tokenService.getCurrentUser();
                        setState({
                            isAuthenticated: true,
                            isLoading: false,
                            user,
                            error: null,
                            loadingPhase: 'ready',
                        });
                        return;
                    }
                    console.log('Silent refresh failed — falling back to MSAL exchange');
                }
                // No hint, or refresh failed: do the full MSAL → JWT exchange.
                console.log('Exchanging MSAL token for JWT…');
                setState(prev => (Object.assign(Object.assign({}, prev), { loadingPhase: 'exchanging' })));
                yield exchangeMsalToken();
            }
            catch (error) {
                console.error('Auth initialization failed:', error);
                setState(prev => (Object.assign(Object.assign({}, prev), { isLoading: false, loadingPhase: 'ready', error: 'Failed to initialize authentication' })));
            }
        });
        initAuth();
    }, [accounts, isMsalAuthenticated, inProgress, exchangeMsalToken]);
    /**
     * Login via MSAL redirect
     */
    const login = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        setState(prev => (Object.assign(Object.assign({}, prev), { isLoading: true, error: null, loadingPhase: 'exchanging' })));
        try {
            // Use redirect (matches login page)
            yield instance.loginRedirect(loginRequest);
        }
        catch (error) {
            console.error('Login failed:', error);
            setState(prev => (Object.assign(Object.assign({}, prev), { isLoading: false, loadingPhase: 'ready', error: 'Login failed. Please try again.' })));
        }
    }), [instance]);
    /**
     * Logout from both JWT and MSAL
     */
    const logout = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            // Revoke JWT refresh token
            yield tokenService.logout();
            // Clear local state first
            setState({
                isAuthenticated: false,
                isLoading: false,
                user: null,
                error: null,
                loadingPhase: 'ready',
            });
            // Logout from MSAL
            yield instance.logoutRedirect({
                postLogoutRedirectUri: '/login'
            });
        }
        catch (error) {
            console.error('Logout error:', error);
            setState({
                isAuthenticated: false,
                isLoading: false,
                user: null,
                error: null,
                loadingPhase: 'ready',
            });
        }
    }), [instance]);
    /**
     * Refresh authentication
     * Returns true if successful, false if user needs to re-login
     */
    const refreshAuth = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        const token = yield tokenService.refreshToken();
        if (!token) {
            setState(prev => (Object.assign(Object.assign({}, prev), { isAuthenticated: false, error: 'Session expired. Please login again.' })));
            return false;
        }
        return true;
    }), []);
    /**
     * Get access token for API calls
     * Use this in axios interceptors
     */
    const getAccessToken = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        return tokenService.getAccessToken();
    }), []);
    const contextValue = Object.assign(Object.assign({}, state), { login,
        logout,
        refreshAuth,
        getAccessToken });
    return (_jsx(AuthContext.Provider, { value: contextValue, children: children }));
};
/**
 * Hook to access authentication context
 * Must be used within AuthProvider
 */
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
